import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { Profile } from '../../types';
import type { ItemAtividadeGeral } from '../../src/lib/projetos/atividadeGeral';
import { corDoUsuario } from '../../src/components/comentarios/ComentarioTexto';
import { abrirSolicitacao } from '../../src/lib/abrirSolicitacao';

interface AtividadeRecentePanelProps {
  itens: ItemAtividadeGeral[];
  users: Profile[];
  carregando: boolean;
}

const dataCurta = (iso: string): string =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * Só leitura, de propósito: comentar continua sendo uma ação de dentro do projeto
 * (ProjetoAtividadeTimeline.tsx). Aqui o objetivo é bater o olho rápido em tudo que mudou
 * recentemente entre todos os projetos — por isso cada item é resumido numa linha (sem markdown,
 * sem chip de menção, com `truncate`): é uma lista de "o que aconteceu", não o lugar de ler o
 * comentário inteiro. Quem quiser o texto completo clica e abre a origem.
 */
const AtividadeRecentePanel: React.FC<AtividadeRecentePanelProps> = ({ itens, users, carregando }) => {
  const autor = (uid: string | null) => users.find(u => u.id === uid);

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <h2 className="font-bold text-gray-800 dark:text-white mb-2">Atividade recente</h2>

      {carregando ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 size={14} className="animate-spin" /> Carregando...
        </div>
      ) : itens.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">Nada por aqui ainda.</p>
      ) : (
        <ul className="space-y-1">
          {itens.map(item => {
            const u = autor(item.userId);
            const paleta = corDoUsuario(item.userId || '');
            // Capturado numa const antes do ternário de propósito: narrowing de acesso de
            // propriedade não sobrevive dentro do onClick — sobre uma const simples, sobrevive.
            const origem = item.origem;
            const className = `block w-full text-left border-l-2 ${paleta.barra} rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-900/40 transition-colors`;
            const conteudo = (
              <>
                <div className="flex items-center gap-1.5 text-[11px] leading-tight">
                  <span className={`font-bold shrink-0 ${paleta.nome}`}>{u?.full_name?.split(' ')[0] || 'Alguém'}</span>
                  <span className="text-gray-400 truncate min-w-0">{item.projetoNome}</span>
                  <span className="text-gray-300 dark:text-gray-600 shrink-0 ml-auto whitespace-nowrap">{dataCurta(item.createdAt)}</span>
                </div>
                <p className={`text-xs truncate leading-tight ${item.tipo === 'status' ? 'italic text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`}>
                  {item.conteudo}
                </p>
              </>
            );
            return (
              <li key={item.id}>
                {/* Item de tarefa abre o modal da solicitação direto (sem navegar — ver
                    src/lib/abrirSolicitacao.ts). Item do mural do projeto (sem tarefa associada)
                    não tem pra onde abrir modal nenhum, então cai na tela do projeto — único
                    destino que existe pra ele. */}
                {origem.tipo === 'tarefa' ? (
                  <button type="button" onClick={() => abrirSolicitacao(origem.tarefaId)} title={item.conteudo} className={className}>
                    {conteudo}
                  </button>
                ) : (
                  <Link to={`/projetos/${item.projetoId}`} title={item.conteudo} className={className}>
                    {conteudo}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AtividadeRecentePanel;
